import React, { useEffect, useMemo, useRef, useState } from "react";
import { trackIntent } from "@/lib/analytics";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, MinusCircle, PlusCircle, Loader2, Shield, Check, Ticket, X, Compass, Maximize2 } from "lucide-react";
import PanoramaViewer from "@/components/PanoramaViewer";
import Lightbox from "@/components/Lightbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LuxeOverline } from "./LuxeOverline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const gbp = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

// Time slots in half-hour increments, produced as HH:MM 24h strings for the API.
const buildTimeSlots = (startHour, endHour) => {
  const out = [];
  for (let h = startHour; h <= endHour; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    if (h !== endHour) out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
};
const TIME_SLOTS_CHECKIN_TOURING = buildTimeSlots(13, 20); // pitches: 1:00 PM → 8:00 PM (latest arrival)
const TIME_SLOTS_CHECKIN_LODGE = buildTimeSlots(14, 20); // lodges: 2:00 PM → 8:00 PM (latest arrival)
const TIME_SLOTS_CHECKOUT = buildTimeSlots(8, 11); // 8:00 AM → 11:00 AM

const formatTime12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${suffix}`;
};

const Booking = () => {
  const [units, setUnits] = useState([]);
  const [unitType, setUnitType] = useState("lodge");
  const [subType, setSubType] = useState("hardstanding"); // touring category (hardstanding | grass | tent)
  const [pooled, setPooled] = useState(null); // pooled availability per sub_type
  const [startDate, setStartDate] = useState(todayPlus(14));
  const [endDate, setEndDate] = useState(todayPlus(17));
  const [guests, setGuests] = useState(2);
  const [dogs, setDogs] = useState(0);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [availability, setAvailability] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [vehicleReg, setVehicleReg] = useState("");
  const [checkinTime, setCheckinTime] = useState("13:00");
  const [checkoutTime, setCheckoutTime] = useState("11:00");
  const [specialRequests, setSpecialRequests] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null); // {code, referrer_name, discount}
  const [agreedPolicy, setAgreedPolicy] = useState(false);
  const [agreedDogPolicy, setAgreedDogPolicy] = useState(false);

  // Media viewers (360 panorama + lightbox for still photos)
  const [panoramaOpen, setPanoramaOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // null = closed, else start index

  // Pick up ?ref=CODE that was stashed in localStorage by LandingPage on first load.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("wl.referralCode");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.code) setReferralCode({ code: parsed.code });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/units`).then((r) => setUnits(r.data.types || [])).catch(() => {});
  }, []);

  const currentType = useMemo(
    () => units.find((u) => u.type === unitType),
    [units, unitType]
  );

  // ---- Pick-your-pitch map handoff (?pitch=daisy-hs-03&start=…&end=…&guests=…)
  // The guest chose this exact pitch on the interactive map — it's locked to
  // them (backend rejects if it's gone), so we pass unit_id even for touring.
  const [lockedPitch] = useState(() => new URLSearchParams(window.location.search).get("pitch") || "");
  const lockedMeta = useMemo(() => {
    if (!lockedPitch) return null;
    for (const t of units) {
      const m = (t.units || []).find((u) => u.id === lockedPitch);
      if (m) return { ...m, unitType: t.type };
    }
    return null;
  }, [lockedPitch, units]);
  const lockedUnavailable = Boolean(
    lockedPitch && availability && !availability.units.some((u) => u.unit_id === lockedPitch && u.available)
  );

  useEffect(() => {
    if (!lockedPitch) return;
    const q = new URLSearchParams(window.location.search);
    const isLodge = lockedPitch.startsWith("lodge-");
    setUnitType(isLodge ? "lodge" : "touring");
    if (!isLodge) {
      const sub = lockedPitch.includes("-hs-") ? "hardstanding" : lockedPitch.includes("-grass-") ? "grass" : "tent";
      setSubType(sub);
    }
    if (q.get("start")) setStartDate(q.get("start"));
    if (q.get("end")) setEndDate(q.get("end"));
    if (q.get("guests")) setGuests(Math.max(1, parseInt(q.get("guests"), 10) || 2));
    setSelectedUnitId(lockedPitch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPitch]);

  const fetchAvailAndQuote = async () => {
    if (!startDate || !endDate) return;
    if (new Date(endDate) <= new Date(startDate)) {
      setAvailability(null);
      setQuote(null);
      return;
    }
    setLoadingAvail(true);
    const seq = ++fetchSeqRef.current;
    try {
      const isTouring = unitType === "touring";
      const [availRes, pooledRes, quoteRes] = await Promise.all([
        axios.get(`${API}/availability`, {
          params: { unit_type: unitType, start_date: startDate, end_date: endDate },
        }),
        isTouring
          ? axios.get(`${API}/availability/pooled`, {
              params: { start_date: startDate, end_date: endDate },
            })
          : Promise.resolve({ data: null }),
        axios.post(`${API}/quote`, {
          unit_type: unitType,
          unit_id: isTouring && !lockedPitch ? undefined : (selectedUnitId || undefined),
          sub_type: isTouring ? subType : undefined,
          start_date: startDate,
          end_date: endDate,
          guests,
          dogs: unitType === "lodge" ? dogs : 0,
          voucher_code: appliedVoucher?.code || undefined,
          referral_code: referralCode?.code || undefined,
          email: guestEmail || undefined,
        }),
      ]);
      if (seq !== fetchSeqRef.current) return; // superseded by a newer search
      setAvailability(availRes.data);
      setPooled(pooledRes?.data || null);
      setQuote(quoteRes.data);
      // If the stored referral code is invalid (e.g. self-referral), clear it silently.
      if (referralCode && quoteRes.data?.referral_error && !quoteRes.data?.referral_code) {
        setReferralCode(null);
        try { window.localStorage.removeItem("wl.referralCode"); } catch { /* ignore */ }
      }
      const firstAvailable = availRes.data.units.find((u) => u.available);
      setSelectedUnitId((prev) => {
        if (lockedPitch) return prev; // map-locked: never auto-correct to another unit
        const stillOk = availRes.data.units.find((u) => u.unit_id === prev && u.available);
        return stillOk ? prev : firstAvailable?.unit_id || "";
      });
    } catch (err) {
      if (seq !== fetchSeqRef.current) return; // superseded
      const detail = err?.response?.data?.detail;
      setAvailability(null);
      setQuote(null);
      if (typeof detail === "string") toast.error(detail);
    } finally {
      if (seq === fetchSeqRef.current) setLoadingAvail(false);
    }
  };

  // The quote auto-fetches on mount with default dates — the high-intent signal
  // is a visitor *changing* their search, so skip the first (automatic) run.
  const firstSearchRef = useRef(true);
  // Stale-response guard: only the latest fetch may write state — otherwise a
  // slow earlier request (e.g. the mount-time lodge search) can land after the
  // pick-your-pitch handoff and clobber the touring availability.
  const fetchSeqRef = useRef(0);
  useEffect(() => {
    fetchAvailAndQuote();
    if (firstSearchRef.current) {
      firstSearchRef.current = false;
    } else {
      trackIntent("availability_check");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType, subType, startDate, endDate, guests, dogs, selectedUnitId, appliedVoucher, referralCode, guestEmail]);

  // Keep arrival time within the allowed window for the unit type — lodges
  // 2PM–8PM, pitches 1PM–8PM. Runs on mount too: lodge is the default unit type
  // and the default 13:00 state isn't a valid lodge slot.
  useEffect(() => {
    const slots = unitType === "lodge" ? TIME_SLOTS_CHECKIN_LODGE : TIME_SLOTS_CHECKIN_TOURING;
    if (!slots.includes(checkinTime)) setCheckinTime(slots[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType]);

  // "booking_form" intent — visitor has been looking at the booking section
  // for a couple of seconds (engaged, not just scrolling past).
  const sectionRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let timer = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible && !timer) {
          timer = setTimeout(() => trackIntent("booking_form"), 2000);
        } else if (!visible && timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const applyVoucher = async () => {
    const code = (voucherCode || "").trim().toUpperCase();
    setVoucherError("");
    if (!code) {
      setVoucherError("Enter a voucher code first.");
      return;
    }
    setVoucherLoading(true);
    try {
      const isTouring = unitType === "touring";
      const { data } = await axios.post(`${API}/quote`, {
        unit_type: unitType,
        unit_id: isTouring && !lockedPitch ? undefined : (selectedUnitId || undefined),
        sub_type: isTouring ? subType : undefined,
        start_date: startDate,
        end_date: endDate,
        guests,
        dogs: unitType === "lodge" ? dogs : 0,
        voucher_code: code,
        email: guestEmail || undefined,
      });
      if (data.voucher_error) {
        setVoucherError(data.voucher_error);
        setAppliedVoucher(null);
      } else if (data.voucher_code) {
        setAppliedVoucher({
          code: data.voucher_code,
          discount: data.voucher_discount,
          free_date: data.voucher_free_date,
        });
        toast.success(`Voucher applied — one free night on ${data.voucher_free_date}.`);
      } else {
        setVoucherError("Voucher could not be applied.");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      setVoucherError(typeof d === "string" ? d : "Could not apply voucher.");
    } finally {
      setVoucherLoading(false);
    }
  };

  const removeVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCode("");
    setVoucherError("");
  };

  const isTouring = unitType === "touring";
  const pooledInfo = isTouring ? pooled?.touring?.[subType] : null;
  const pooledAvail = pooledInfo?.available ?? 0;

  const canBook =
    quote &&
    availability &&
    availability.available_count > 0 &&
    (isTouring ? pooledAvail > 0 : !!selectedUnitId) &&
    guestName.trim() &&
    guestEmail.trim() &&
    agreedPolicy &&
    !lockedUnavailable &&
    (Number(dogs) === 0 || agreedDogPolicy);

  const submit = async () => {
    if (!canBook) {
      toast.error("Please pick available dates and fill your details.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/checkout`, {
        unit_type: unitType,
        unit_id: isTouring && !lockedPitch ? undefined : selectedUnitId,
        sub_type: isTouring ? subType : undefined,
        start_date: startDate,
        end_date: endDate,
        guests: Number(guests),
        dogs: unitType === "lodge" ? Number(dogs) : 0,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        vehicle_reg: vehicleReg,
        checkin_time: checkinTime,
        checkout_time: checkoutTime,
        special_requests: specialRequests.trim() || undefined,
        origin_url: window.location.origin,
        voucher_code: appliedVoucher?.code || undefined,
        referral_code: referralCode?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg
        : "Could not start checkout — please check your details and try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const fieldClass =
    "bg-[#F5EFE6] border-[#DDD1BE] focus-visible:ring-2 focus-visible:ring-[#0F0E0E] focus-visible:border-[#0F0E0E] rounded-lg h-11";

  return (
    <section
      ref={sectionRef}
      id="book"
      data-testid="booking-section"
      className="relative py-24 md:py-36 bg-[#EAE0D0] overflow-hidden scroll-mt-16"
    >
      {/* Ambient rose-gold glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(228,178,168,0.35) 0%, transparent 60%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <LuxeOverline>Book your stay</LuxeOverline>
            <h2 className="font-serif text-4xl md:text-5xl mt-8 text-[#14100F] leading-[1.05]">
              Hold your dates with a{" "}
              <em className="italic rose-gold-text">25% deposit.</em>
            </h2>
            <p className="mt-6 text-[#3A322E] text-base md:text-lg leading-relaxed">
              Booking more than 14 days ahead? A 25% deposit secures your stay.
              Booking within 14 days? Pay in full for a smooth, quick check-in.
              Either way you&rsquo;re covered by our flexible policy — full refund
              within 72 hours of booking, and again if you cancel more than 14
              days out (minus a 10% admin fee).
            </p>

            <div className="mt-10 space-y-4 text-sm text-[#6A5F58]">
              <div className="flex items-center gap-3">
                <CalendarDays size={18} className="text-[#0F0E0E]" strokeWidth={1.4} />
                Season runs mid-March through end of November.
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-2 w-1 h-1 rounded-full bg-[#B76E79] flex-shrink-0" />
                Lodges: 2-night minimum · sleeps up to 4 (2 included, +£15 per extra guest per stay) · dogs welcome (up to 2 — £30 for 1 dog, £45 for 2 per stay · on a lead at all times) · fishing included · check-in from 2:00 PM · refundable £100 card hold on arrival.
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-2 w-1 h-1 rounded-full bg-[#B76E79] flex-shrink-0" />
                Camping: 1-night minimum · gravel & grass w/ EHU £30 · tent pitches £24 · sleeps up to 4 (2 included, +£5 per extra guest/night) · EHU includes 15 kWh/day, then 33p/kWh by prepaid top-up card.
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div
              data-testid="booking-widget"
              className="relative bg-white border border-[#DDD1BE] rounded-2xl p-8 md:p-10 shadow-xl overflow-hidden"
            >
              {/* Top rose-gold accent bar */}
              <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E]" />
              {lockedPitch && (
                <div className="mb-6 rounded-xl border border-[#B76E79]/40 bg-[#B76E79]/5 px-4 py-3" data-testid="locked-pitch-banner">
                  {lockedUnavailable ? (
                    <p className="text-sm text-[#8C4E58]">
                      Sorry — {lockedMeta?.name || "that pitch"} was just taken for those dates. Try different dates, or{" "}
                      <a href="/pick-your-pitch" className="underline underline-offset-2">pick another pitch on the map</a>.
                    </p>
                  ) : (
                    <p className="text-sm text-[#3A322E]">
                      <Check size={14} className="inline mr-1.5 -mt-0.5 text-[#3B8C4E]" />
                      You're booking <strong>{lockedMeta?.name || lockedPitch}</strong> — your exact pick from the map,
                      guaranteed yours for these dates.{" "}
                      <a href="/pick-your-pitch" className="text-[#B76E79] underline underline-offset-2">Change pitch</a>
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Accommodation
                  </Label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {units.map((u) => {
                      const active = u.type === unitType;
                      return (
                        <button
                          key={u.type}
                          type="button"
                          data-testid={`booking-unit-${u.type}`}
                          disabled={!!lockedPitch}
                          onClick={() => {
                            if (lockedPitch) return; // map-locked — "Change pitch" link switches instead
                            setUnitType(u.type);
                            setSelectedUnitId("");
                            if (u.type !== "lodge") setDogs(0);
                            // Lodges check in from 2PM; pitches from 1PM — latest arrival 8PM
                            if (u.type === "lodge" && checkinTime < "14:00") setCheckinTime("14:00");
                          }}
                          className={`relative text-left p-4 rounded-xl border transition-all overflow-hidden ${
                            active
                              ? "border-[#B76E79] bg-gradient-to-br from-[#0F0E0E] to-[#2a1a1c] text-[#F5EFE6] shadow-lg"
                              : "border-[#DDD1BE] bg-[#F5EFE6] hover:border-[#B76E79]/60 hover:shadow-md"
                          }`}
                        >
                          {active && (
                            <span className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E]" />
                          )}
                          <div className="text-sm font-medium">{u.name}</div>
                          <div
                            className={`text-xs mt-1 ${
                              active ? "text-[#E4B2A8]" : "text-[#6A5F58]"
                            }`}
                          >
                            from {gbp(u.nightly)} / night · sleeps {u.capacity}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="b-arrival" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Arrival
                  </Label>
                  <Input
                    id="b-arrival"
                    type="date"
                    min={todayPlus(0)}
                    data-testid="booking-start"
                    className={`mt-2 ${fieldClass}`}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="b-depart" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Departure
                  </Label>
                  <Input
                    id="b-depart"
                    type="date"
                    min={startDate}
                    data-testid="booking-end"
                    className={`mt-2 ${fieldClass}`}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Guests (15+)
                  </Label>
                  <div className="mt-2 flex items-center h-11 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] justify-between px-3">
                    <button
                      type="button"
                      data-testid="booking-guests-decr"
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      className="text-[#0F0E0E] disabled:opacity-40"
                      disabled={guests <= 1}
                    >
                      <MinusCircle size={20} strokeWidth={1.4} />
                    </button>
                    <span className="font-medium text-[#14100F]" data-testid="booking-guests-value">
                      {guests}
                    </span>
                    <button
                      type="button"
                      data-testid="booking-guests-incr"
                      onClick={() =>
                        setGuests(Math.min(currentType?.capacity || 6, guests + 1))
                      }
                      className="text-[#0F0E0E]"
                      disabled={guests >= (currentType?.capacity || 6)}
                    >
                      <PlusCircle size={20} strokeWidth={1.4} />
                    </button>
                  </div>
                  {/* Live extra-guest hint */}
                  {(() => {
                    const extra = Math.max(0, guests - 2);
                    const isLodge = unitType === "lodge";
                    const perExtra = isLodge ? 15 : 5;
                    const nights = Math.max(
                      0,
                      Math.floor((new Date(endDate) - new Date(startDate)) / 86400000)
                    );
                    if (extra === 0) {
                      return (
                        <p
                          className="mt-2 text-[11px] text-[#6A5F58] leading-snug"
                          data-testid="booking-guests-hint"
                        >
                          Base rate includes 2 guests · +£{perExtra} per extra guest{isLodge ? " (flat per stay)" : "/night"}
                        </p>
                      );
                    }
                    const supplement = isLodge
                      ? extra * perExtra
                      : extra * perExtra * (nights || 1);
                    return (
                      <p
                        className="mt-2 text-[11px] leading-snug text-[#B76E79] font-medium"
                        data-testid="booking-guests-hint"
                      >
                        +{extra} extra guest{extra > 1 ? "s" : ""}: +£{supplement.toFixed(0)}
                        {isLodge
                          ? " (flat, per stay)"
                          : nights
                          ? ` (£${perExtra} × ${extra} × ${nights} night${nights > 1 ? "s" : ""})`
                          : ` (£${perExtra} × ${extra}/night)`}
                      </p>
                    );
                  })()}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    {isTouring ? "Choose pitch type" : "Preferred lodge"}
                  </Label>
                  {isTouring ? (
                    <div className="mt-3 space-y-2.5" data-testid="booking-subtype-tiles">
                      {["hardstanding", "grass", "tent"].map((k) => {
                        const info = pooled?.touring?.[k];
                        const active = subType === k;
                        const total = info?.total ?? "—";
                        const avail = info?.available ?? "—";
                        const soldOut = info && info.available === 0;
                        return (
                          <button
                            key={k}
                            type="button"
                            data-testid={`booking-subtype-${k}`}
                            onClick={() => !soldOut && setSubType(k)}
                            disabled={soldOut}
                            className={`w-full text-left rounded-xl border transition-all group flex items-stretch overflow-hidden ${
                              active
                                ? "border-[#B76E79] bg-[#F5EFE6] shadow-[0_10px_24px_-14px_rgba(183,110,121,0.55)]"
                                : "border-[#DDD1BE] bg-white hover:border-[#B76E79]"
                            } ${soldOut ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                          >
                            {/* Left accent strip */}
                            <span
                              className={`w-1.5 flex-shrink-0 transition-colors ${
                                active ? "bg-[#B76E79]" : "bg-[#DDD1BE] group-hover:bg-[#B76E79]"
                              }`}
                            />
                            <div className="flex-1 flex items-center gap-4 p-4">
                              {/* Radio indicator */}
                              <span
                                className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                  active ? "border-[#B76E79]" : "border-[#C9BFB1] group-hover:border-[#B76E79]"
                                }`}
                                aria-hidden
                              >
                                {active && <span className="w-1.5 h-1.5 rounded-full bg-[#B76E79]" />}
                              </span>
                              {/* Title + blurb */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <p className="text-[11px] uppercase tracking-[0.20em] text-[#B76E79] font-semibold">
                                    {info?.label || (k[0].toUpperCase() + k.slice(1) + " Pitch")}
                                  </p>
                                  {info?.electric && (
                                    <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6]">EHU</span>
                                  )}
                                </div>
                                <p className="text-[13px] text-[#6A5F58] mt-1 leading-relaxed">
                                  {info?.blurb}
                                </p>
                              </div>
                              {/* Price + availability */}
                              <div className="text-right flex-shrink-0">
                                <p className="font-serif text-xl md:text-2xl text-[#14100F] leading-none">
                                  £{(info?.nightly ?? "—").toString()}
                                  <span className="text-xs text-[#6A5F58] font-sans font-normal"> /nt</span>
                                </p>
                                <p className={`mt-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${soldOut ? "text-[#8C2E39]" : "text-[#3B8C4E]"}`}>
                                  {soldOut ? "Sold out" : `${avail} of ${total} left`}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <Select
                      value={selectedUnitId}
                      onValueChange={setSelectedUnitId}
                      disabled={!availability || availability.available_count === 0 || !!lockedPitch}
                    >
                      <SelectTrigger
                        data-testid="booking-unit-select"
                        className={`mt-2 ${fieldClass}`}
                      >
                        <SelectValue placeholder="Select a lodge" />
                      </SelectTrigger>
                      <SelectContent>
                        {(availability?.units || []).map((u) => {
                          const meta = currentType?.units?.find((m) => m.id === u.unit_id);
                          const price = meta?.nightly;
                          return (
                            <SelectItem
                              key={u.unit_id}
                              value={u.unit_id}
                              disabled={!u.available}
                            >
                              {u.unit_name}
                              {price ? ` — £${price}/nt` : ""}
                              {u.available ? "" : " · unavailable"}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                  {isTouring && (
                    <p className="mt-2 text-[11px] text-[#6A5F58] leading-snug">
                      Your specific pitch number is allocated on arrival based on site conditions and any special requests you&rsquo;ve made below.
                    </p>
                  )}

                  {/* Media preview for the selected pitch */}
                  {(() => {
                    // For lodges: show the selected lodge's media.
                    // For touring: show the first pitch of the chosen sub_type as a representative preview.
                    let meta;
                    if (isTouring) {
                      meta = currentType?.units?.find((m) => (m.sub_type || "").toLowerCase() === subType && ((m.images && m.images.length) || m.video_url || m.panorama_url || m.description));
                    } else {
                      meta = currentType?.units?.find((m) => m.id === selectedUnitId);
                    }
                    if (!meta) return null;
                    const hasMedia = (meta.images && meta.images.length) || meta.video_url || meta.description || meta.panorama_url;
                    if (!hasMedia) return null;
                    const isEmbed = meta.video_url && /youtube\.com|youtu\.be|vimeo\.com/i.test(meta.video_url);
                    const ytId = meta.video_url && (meta.video_url.match(/[?&]v=([^&]+)/) || meta.video_url.match(/youtu\.be\/([^?&]+)/) || meta.video_url.match(/vimeo\.com\/(\d+)/));
                    const embedSrc = isEmbed && ytId ? (
                      meta.video_url.includes("vimeo.com")
                        ? `https://player.vimeo.com/video/${ytId[1]}?title=0&byline=0&portrait=0`
                        : `https://www.youtube.com/embed/${ytId[1]}?rel=0&modestbranding=1`
                    ) : null;
                    return (
                      <div className="mt-4 bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-6 shadow-[0_10px_30px_-15px_rgba(15,14,14,0.15)]" data-testid="pitch-media-preview">
                        <div className="flex items-baseline justify-between gap-3 mb-4">
                          <p className="text-[10px] uppercase tracking-[0.22em] text-[#B76E79]">
                            {isTouring ? `Your ${(pooled?.touring?.[subType]?.label || "pitch").toLowerCase()}` : "Your pitch"}
                          </p>
                          <p className="font-serif text-base md:text-lg text-[#14100F] truncate">
                            {isTouring ? (pooled?.touring?.[subType]?.label || meta.name) : meta.name}
                          </p>
                        </div>
                        {meta.panorama_url && (
                          <button
                            type="button"
                            onClick={() => setPanoramaOpen(true)}
                            className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-3 md:py-4 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs md:text-sm uppercase tracking-[0.18em]"
                            data-testid="pitch-panorama-open"
                          >
                            <Compass size={17} strokeWidth={1.8} />
                            Take the 360° tour — drag to look around
                          </button>
                        )}
                        {meta.images && meta.images.length > 0 && (() => {
                          const imgs = meta.images;
                          const n = imgs.length;
                          const thumb = (src, i, extraClass = "") => (
                            <button
                              type="button"
                              key={`${src}-${i}`}
                              onClick={() => setLightboxIdx(i)}
                              className={`relative rounded-xl border border-[#DDD1BE] overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#B76E79] bg-[#F5EFE6] ${extraClass}`}
                              aria-label={`Open photo ${i + 1}`}
                              data-testid={`pitch-media-thumb-${i}`}
                            >
                              <img
                                src={src}
                                alt={`${meta.name} ${i + 1}`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
                              />
                              <span className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                <Maximize2 size={11} strokeWidth={2} />
                                View
                              </span>
                            </button>
                          );
                          // 1 photo → single hero, 16:9
                          if (n === 1) {
                            return <div className="aspect-video">{thumb(imgs[0], 0, "w-full h-full")}</div>;
                          }
                          // 2 photos → side-by-side, equal 16:9
                          if (n === 2) {
                            return (
                              <div className="grid grid-cols-2 gap-2 md:gap-3" data-testid="pitch-media-gallery">
                                {imgs.map((s, i) => (
                                  <div key={i} className="aspect-video">{thumb(s, i, "w-full h-full")}</div>
                                ))}
                              </div>
                            );
                          }
                          // 3 photos → hero + 2 stacked thumbs on the right
                          if (n === 3) {
                            return (
                              <div className="grid grid-cols-3 gap-2 md:gap-3 aspect-[16/9]" data-testid="pitch-media-gallery">
                                <div className="col-span-2 h-full">{thumb(imgs[0], 0, "w-full h-full")}</div>
                                <div className="flex flex-col gap-2 md:gap-3 h-full">
                                  <div className="flex-1">{thumb(imgs[1], 1, "w-full h-full")}</div>
                                  <div className="flex-1">{thumb(imgs[2], 2, "w-full h-full")}</div>
                                </div>
                              </div>
                            );
                          }
                          // 4+ → Airbnb-style: big hero + 2x2 grid of 4 thumbs (extras click through to full gallery)
                          const extras = n - 5;
                          return (
                            <div className="grid grid-cols-4 grid-rows-2 gap-2 md:gap-3 aspect-[16/9]" data-testid="pitch-media-gallery">
                              <div className="col-span-2 row-span-2 h-full">{thumb(imgs[0], 0, "w-full h-full")}</div>
                              {[1, 2, 3, 4].map((i) =>
                                imgs[i] ? (
                                  <div key={i} className="relative h-full">
                                    {thumb(imgs[i], i, "w-full h-full")}
                                    {i === 4 && extras > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => setLightboxIdx(4)}
                                        className="absolute inset-0 rounded-xl bg-black/55 text-white flex items-center justify-center text-lg font-serif hover:bg-black/70 transition-colors"
                                        data-testid="pitch-media-more"
                                      >
                                        +{extras} more
                                      </button>
                                    )}
                                  </div>
                                ) : null,
                              )}
                            </div>
                          );
                        })()}
                        {meta.video_url && (
                          <div className="mt-4 rounded-xl overflow-hidden border border-[#DDD1BE] bg-black" data-testid="pitch-media-video">
                            {embedSrc ? (
                              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                                <iframe
                                  src={embedSrc}
                                  title={`${meta.name} video`}
                                  className="absolute inset-0 w-full h-full"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            ) : (
                              <video src={meta.video_url} controls className="w-full h-auto" />
                            )}
                          </div>
                        )}
                        {meta.description && (
                          <p className="mt-4 text-sm md:text-[15px] text-[#3A322E] leading-relaxed">{meta.description}</p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Dogs selector (lodges only) */}
                {unitType === "lodge" && (
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                      Bringing dogs? (£30 for 1 dog · £45 for 2 · cleaning fee per stay)
                    </Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {[0, 1, 2].map((n) => {
                        const active = dogs === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`booking-dogs-${n}`}
                            onClick={() => setDogs(n)}
                            className={`h-11 rounded-lg border text-sm font-medium transition-all ${
                              active
                                ? "border-[#B76E79] bg-gradient-to-br from-[#0F0E0E] to-[#2a1a1c] text-[#F5EFE6] shadow-md"
                                : "border-[#DDD1BE] bg-[#F5EFE6] text-[#3A322E] hover:border-[#B76E79]/60"
                            }`}
                          >
                            {n === 0 ? "No dogs" : `${n} dog${n > 1 ? "s" : ""}`}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-[#6A5F58] leading-relaxed">
                      Up to 2 dogs per lodge · no dangerous breeds · dogs must be kept on a lead at all times on site.
                    </p>
                    {dogs > 0 && (
                      <label
                        className="mt-3 flex items-start gap-3 cursor-pointer group rounded-lg border border-[#DDD1BE] bg-white/60 p-3"
                        data-testid="booking-dog-policy-agree-label"
                      >
                        <input
                          type="checkbox"
                          data-testid="booking-dog-policy-agree"
                          checked={agreedDogPolicy}
                          onChange={(e) => setAgreedDogPolicy(e.target.checked)}
                          className="mt-[2px] w-4 h-4 accent-[#B76E79] cursor-pointer"
                        />
                        <span className="text-[12px] md:text-[13px] text-[#3A322E] leading-relaxed group-hover:text-[#0F0E0E] transition-colors">
                          I agree to keep my dog on a lead at all times, bring my own dog bed and blanket,
                          and keep my dog off the furniture — or cover any furniture with my own blanket if they do go on it.
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>

              <div
                className="mt-8 p-6 rounded-xl bg-[#EAE0D0] border border-[#DDD1BE]"
                data-testid="booking-summary"
              >
                {loadingAvail ? (
                  <div className="flex items-center gap-2 text-[#6A5F58] text-sm">
                    <Loader2 size={16} className="animate-spin" /> Checking availability...
                  </div>
                ) : quote && availability ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                          Nights
                        </div>
                        <div className="font-serif text-2xl text-[#14100F] mt-1">
                          {quote.nights}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                          Available
                        </div>
                        <div
                          className={`font-serif text-2xl mt-1 ${
                            availability.available_count > 0
                              ? "text-[#0F0E0E]"
                              : "text-[#8C2E39]"
                          }`}
                          data-testid="booking-available-count"
                        >
                          {availability.available_count} / {availability.units.length}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                          Total stay
                        </div>
                        <div className="font-serif text-2xl text-[#14100F] mt-1" data-testid="booking-total">
                          {gbp(quote.total)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[#B76E79]" data-testid="booking-payable-label">
                          {quote.payment_type === "full" ? "Pay now (full)" : "Deposit (25%)"}
                        </div>
                        <div className="font-serif text-2xl text-[#B76E79] mt-1" data-testid="booking-deposit">
                          {gbp(quote.payable_now ?? quote.deposit)}
                        </div>
                      </div>
                    </div>

                    {/* Breakdown when extra-guest fee, dog fee, or voucher applies */}
                    {(quote.extra_guests > 0 || quote.dog_fee > 0 || quote.voucher_discount > 0 || quote.referral_discount > 0) && (
                      <div
                        data-testid="booking-breakdown"
                        className="text-xs md:text-sm bg-[#F5EFE6] border border-[#DDD1BE] rounded-lg p-3 md:p-4 space-y-1"
                      >
                        <div className="flex justify-between text-[#3A322E]">
                          <span>
                            {quote.nights} × {gbp(quote.nightly)} (base for 2 guests)
                          </span>
                          <span className="font-medium">{gbp(quote.base_total)}</span>
                        </div>
                        {quote.extra_guests > 0 && (
                          <div className="flex justify-between text-[#B76E79]">
                            <span>
                              {quote.unit_type === "lodge" ? (
                                <>
                                  +{quote.extra_guests} extra guest{quote.extra_guests > 1 ? "s" : ""} × £{(quote.extra_guest_fee / quote.extra_guests).toFixed(0)} (per stay)
                                </>
                              ) : (
                                <>
                                  +{quote.extra_guests} extra guest{quote.extra_guests > 1 ? "s" : ""} × £{(quote.extra_guest_fee / (quote.extra_guests * quote.nights)).toFixed(0)} × {quote.nights} night{quote.nights > 1 ? "s" : ""}
                                </>
                              )}
                            </span>
                            <span className="font-medium">+{gbp(quote.extra_guest_fee)}</span>
                          </div>
                        )}
                        {quote.dog_fee > 0 && (
                          <div className="flex justify-between text-[#B76E79]" data-testid="booking-dog-fee-line">
                            <span>
                              Dog cleaning fee ({quote.dogs} dog{quote.dogs > 1 ? "s" : ""}, per stay)
                            </span>
                            <span className="font-medium">+{gbp(quote.dog_fee)}</span>
                          </div>
                        )}
                        {quote.voucher_discount > 0 && (
                          <div className="flex justify-between text-[#0F0E0E] pt-1 border-t border-[#DDD1BE]" data-testid="booking-voucher-line">
                            <span>
                              Voucher {quote.voucher_code} — free night on {quote.voucher_free_date}
                            </span>
                            <span className="font-semibold text-[#0F0E0E]">−{gbp(quote.voucher_discount)}</span>
                          </div>
                        )}
                        {quote.referral_discount > 0 && (
                          <div className="flex justify-between text-[#0F0E0E] pt-1 border-t border-[#DDD1BE]" data-testid="booking-referral-line">
                            <span>
                              Friend-referral discount{quote.referral_referrer_name ? ` (thanks to ${quote.referral_referrer_name})` : ""}
                            </span>
                            <span className="font-semibold text-[#0F0E0E]">−{gbp(quote.referral_discount)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-[#6A5F58]">
                    Choose your dates to see the total and deposit.
                  </div>
                )}
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="b-name" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Full name *
                  </Label>
                  <Input
                    id="b-name"
                    data-testid="booking-name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className={`mt-2 ${fieldClass}`}
                    placeholder="Jane Appleby"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="b-email" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Email *
                  </Label>
                  <Input
                    id="b-email"
                    type="email"
                    data-testid="booking-email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className={`mt-2 ${fieldClass}`}
                    placeholder="jane@example.co.uk"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="b-phone" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Phone *
                  </Label>
                  <Input
                    id="b-phone"
                    type="tel"
                    data-testid="booking-phone"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className={`mt-2 ${fieldClass}`}
                    placeholder="07XXX XXX XXX"
                    required
                    minLength={6}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="b-vreg" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Vehicle registration *
                  </Label>
                  <Input
                    id="b-vreg"
                    data-testid="booking-vehicle-reg"
                    value={vehicleReg}
                    onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                    className={`mt-2 ${fieldClass} tracking-[0.14em] font-mono uppercase`}
                    placeholder="AB12 CDE"
                    required
                    minLength={2}
                    maxLength={20}
                  />
                  <p className="mt-1.5 text-[11px] text-[#6A5F58]">Required for site access · one vehicle per pitch/lodge.</p>
                </div>
                <div>
                  <Label htmlFor="b-checkin-time" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Check-in time *
                  </Label>
                  <select
                    id="b-checkin-time"
                    data-testid="booking-checkin-time"
                    value={checkinTime}
                    onChange={(e) => setCheckinTime(e.target.value)}
                    className={`mt-2 w-full px-3 ${fieldClass}`}
                    required
                  >
                    {(unitType === "lodge" ? TIME_SLOTS_CHECKIN_LODGE : TIME_SLOTS_CHECKIN_TOURING).map((t) => (
                      <option key={t} value={t}>{formatTime12h(t)}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-[11px] text-[#6A5F58]">
                    {unitType === "lodge"
                      ? "Lodges from 2:00 PM · latest arrival 8:00 PM."
                      : "Pitches from 1:00 PM · latest arrival 8:00 PM."}
                  </p>
                </div>
                <div>
                  <Label htmlFor="b-checkout-time" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Check-out time *
                  </Label>
                  <select
                    id="b-checkout-time"
                    data-testid="booking-checkout-time"
                    value={checkoutTime}
                    onChange={(e) => setCheckoutTime(e.target.value)}
                    className={`mt-2 w-full px-3 ${fieldClass}`}
                    required
                  >
                    {TIME_SLOTS_CHECKOUT.map((t) => (
                      <option key={t} value={t}>{formatTime12h(t)}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-[11px] text-[#6A5F58]">Please vacate by 11:00 AM.</p>
                </div>
              </div>

              {/* Special requests — optional free-text field */}
              <div className="mt-6">
                <Label htmlFor="b-special-requests" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                  Special requests (optional)
                </Label>
                <textarea
                  id="b-special-requests"
                  data-testid="booking-special-requests"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value.slice(0, 500))}
                  rows={3}
                  maxLength={500}
                  placeholder={isTouring
                    ? "e.g. 'Please try to allocate Daisy 3 if possible — close to the lake.' Any accessibility, layout, or arrival notes."
                    : "e.g. 'Celebrating our anniversary — would love a bottle in the fridge.' Any accessibility, dietary or arrival notes."}
                  className="mt-2 w-full rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 py-2 text-sm text-[#14100F] focus:outline-none focus:ring-2 focus:ring-[#B76E79] resize-none"
                />
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-[#6A5F58]">
                  <span>
                    {isTouring
                      ? "Your specific pitch number is allocated on arrival. We'll do our best to honour requests."
                      : "We'll do our best to honour any requests you leave here."}
                  </span>
                  <span className="tabular-nums">{specialRequests.length}/500</span>
                </div>
              </div>

              {/* Voucher / free-night code */}
              <div className="mt-6 rounded-xl border border-dashed border-[#B76E79]/70 bg-[#FBF7F1] p-4 md:p-5" data-testid="voucher-block">
                <div className="flex items-center gap-2 text-[#0F0E0E]">
                  <Ticket size={16} className="text-[#B76E79]" strokeWidth={1.6} />
                  <span className="font-serif text-base md:text-lg">Free-night voucher</span>
                </div>
                {appliedVoucher ? (
                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-[#3A322E]">
                        <span className="font-mono font-bold tracking-[0.08em]">{appliedVoucher.code}</span>{" "}
                        applied — <strong>£{Number(appliedVoucher.discount || 0).toFixed(2)}</strong> off
                        {appliedVoucher.free_date ? ` (free night on ${appliedVoucher.free_date})` : ""}.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removeVoucher}
                      data-testid="voucher-remove"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#6A5F58] hover:text-[#0F0E0E]"
                    >
                      <X size={13} /> Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="mt-1 text-[13px] text-[#6A5F58]">
                      Got a Walnut Lakes reward code? Enter it below — one free night applies to the first eligible weekday (Mon–Thu, no bank holidays).
                    </p>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <Input
                        data-testid="voucher-input"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                        className={`flex-1 ${fieldClass} font-mono tracking-[0.10em]`}
                        placeholder="WL-XXXXXX"
                        maxLength={20}
                      />
                      <button
                        type="button"
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucherCode.trim()}
                        data-testid="voucher-apply"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
                      >
                        {voucherLoading ? <Loader2 size={13} className="animate-spin" /> : <Ticket size={13} />}
                        Apply
                      </button>
                    </div>
                    {voucherError && (
                      <p className="mt-2 text-xs text-[#B76E79]" data-testid="voucher-error">{voucherError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Inline booking policy — always visible before the Pay button */}
              <div
                data-testid="booking-policy"
                className="mt-8 rounded-xl border border-[#DDD1BE] bg-[#F5EFE6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2 text-[#0F0E0E]">
                  <Shield size={16} className="text-[#B76E79]" strokeWidth={1.6} />
                  <span className="font-serif text-lg md:text-xl">Peace-of-mind booking policy</span>
                </div>
                <ul className="mt-4 space-y-2.5 text-[13px] md:text-sm text-[#3A322E] leading-relaxed">
                  <li className="flex gap-2">
                    <Check size={15} className="mt-[3px] text-[#B76E79] flex-shrink-0" strokeWidth={2} />
                    <span>
                      <strong className="text-[#0F0E0E]">Check-in: pitches from 1:00 PM, lodges from 2:00 PM · Check-out by 11:00 AM.</strong>{" "}
                      Latest arrival time is 8:00 PM.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-[3px] text-[#B76E79] flex-shrink-0" strokeWidth={2} />
                    <span>
                      <strong className="text-[#0F0E0E]">Deposit or full payment.</strong>{" "}
                      More than 14 days before arrival — 25% deposit secures your dates.
                      Within 14 days — full payment for a smooth, keys-in-hand check-in.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-[3px] text-[#B76E79] flex-shrink-0" strokeWidth={2} />
                    <span>
                      <strong className="text-[#0F0E0E]">72-hour change of mind.</strong>{" "}
                      Cancel within 72 hours of booking for a <em>full refund</em>, no questions asked.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-[3px] text-[#B76E79] flex-shrink-0" strokeWidth={2} />
                    <span>
                      <strong className="text-[#0F0E0E]">Cancelling later.</strong>{" "}
                      More than 14 days before arrival — full refund, minus a 10% admin fee.
                      Within 14 days — 12-month credit voucher (or a 50% refund at our discretion).
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={15} className="mt-[3px] text-[#B76E79] flex-shrink-0" strokeWidth={2} />
                    <span>
                      <strong className="text-[#0F0E0E]">Unforeseen circumstances.</strong>{" "}
                      Full refund or free rescheduling for vehicle breakdown (AA/RAC receipt),
                      illness (doctor&rsquo;s note), bereavement, or any other genuine emergency at our discretion.
                    </span>
                  </li>
                </ul>

                <label
                  className="mt-5 flex items-start gap-3 cursor-pointer group"
                  data-testid="booking-policy-agree-label"
                >
                  <input
                    type="checkbox"
                    data-testid="booking-policy-agree"
                    checked={agreedPolicy}
                    onChange={(e) => setAgreedPolicy(e.target.checked)}
                    className="mt-[3px] w-4 h-4 accent-[#B76E79] cursor-pointer"
                  />
                  <span className="text-[13px] md:text-sm text-[#3A322E] group-hover:text-[#0F0E0E] transition-colors">
                    I&rsquo;ve read and agree to the Walnut Lakes booking policy above.
                  </span>
                </label>
                <p className="mt-3 text-xs text-[#6A5F58]">
                  Travelling with dogs or rods?{" "}
                  <a href="/policies" target="_blank" rel="noopener noreferrer" className="text-[#B76E79] hover:text-[#0F0E0E] underline underline-offset-2" data-testid="booking-policies-link">
                    Read the full park policies
                  </a>
                  .
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-[#6A5F58]">
                  {quote?.payment_type === "full"
                    ? "You’ll be redirected to Square to pay in full — check-in is smooth once you arrive."
                    : "You’ll be redirected to Square to pay your 25% deposit — the balance is settled on arrival."}
                </p>
                <button
                  type="button"
                  data-testid="booking-submit"
                  onClick={submit}
                  disabled={!canBook || submitting}
                  className="btn-luxe-xl font-brand tracking-[0.18em] w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Redirecting...
                    </>
                  ) : quote ? (
                    quote.payment_type === "full"
                      ? `Pay ${gbp(quote.payable_now)} in full`
                      : `Pay ${gbp(quote.payable_now ?? quote.deposit)} deposit`
                  ) : (
                    "Reserve dates"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Media modals — 360° panorama + still-photo lightbox */}
      {panoramaOpen && (() => {
        const meta = isTouring
          ? currentType?.units?.find((m) => (m.sub_type || "").toLowerCase() === subType && m.panorama_url)
          : currentType?.units?.find((m) => m.id === selectedUnitId);
        if (!meta?.panorama_url) { setPanoramaOpen(false); return null; }
        return (
          <PanoramaViewer
            imageUrl={meta.panorama_url}
            title={meta.name}
            onClose={() => setPanoramaOpen(false)}
          />
        );
      })()}
      {lightboxIdx !== null && (() => {
        const meta = isTouring
          ? currentType?.units?.find((m) => (m.sub_type || "").toLowerCase() === subType && m.images?.length)
          : currentType?.units?.find((m) => m.id === selectedUnitId);
        const imgs = meta?.images || [];
        if (imgs.length === 0) { setLightboxIdx(null); return null; }
        return (
          <Lightbox
            images={imgs}
            startIndex={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
          />
        );
      })()}
    </section>
  );
};

export default Booking;
